<template>
  <!-- Idle state: collapsed card -->
  <div v-if="state === 'idle'" class="inline-quiz-card">
    <div class="inline-quiz-header">
      <div class="inline-quiz-icon-row">
        <span class="inline-quiz-icon">?</span>
        <div>
          <p class="inline-quiz-title">Mini Quiz</p>
          <p class="inline-quiz-count">
            {{ block.questionIds.length }} pregunta{{ block.questionIds.length !== 1 ? 's' : '' }}
          </p>
        </div>
      </div>
      <q-btn
        no-caps
        dense
        unelevated
        label="Comenzar"
        :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', fontSize: '13px', borderRadius: '8px', padding: '6px 16px' }"
        @click="handleStart"
      />
    </div>
  </div>

  <!-- Loading state -->
  <div v-else-if="state === 'loading'" class="inline-quiz-card">
    <div class="inline-quiz-loading">
      <q-spinner-dots color="amber" size="32px" />
    </div>
  </div>

  <!-- Submitted state: results -->
  <div v-else-if="state === 'submitted' && submitResult" class="inline-quiz-card">
    <!-- Score summary -->
    <div class="inline-quiz-header">
      <div class="inline-quiz-icon-row">
        <span class="inline-quiz-icon">?</span>
        <p class="inline-quiz-title">Mini Quiz</p>
      </div>
      <span class="inline-quiz-score-text">
        {{ submitResult.correctAnswers }} de {{ submitResult.totalQuestions }} correctas
      </span>
    </div>

    <!-- Per-question results -->
    <div class="inline-quiz-results">
      <div
        v-for="(r, i) in submitResult.results"
        :key="r.questionId"
        class="inline-quiz-result-card"
      >
        <div class="inline-quiz-result-row">
          <span
            class="inline-quiz-result-badge"
            :class="r.correct ? 'result-correct' : 'result-incorrect'"
          >
            {{ r.correct ? '\u2713' : '\u2717' }}
          </span>
          <div class="inline-quiz-result-body">
            <p class="inline-quiz-result-question">{{ i + 1 }}. {{ r.question }}</p>
            <div class="inline-quiz-result-options">
              <div
                v-for="opt in r.options"
                :key="opt.id"
                class="inline-quiz-result-option"
                :class="{
                  'option-correct': opt.id === r.correctOptionId,
                  'option-wrong': opt.id === r.selectedOptionId && opt.id !== r.correctOptionId,
                }"
              >
                <span
                  class="option-indicator"
                  :class="{
                    'indicator-correct': opt.id === r.correctOptionId,
                    'indicator-wrong': opt.id === r.selectedOptionId && opt.id !== r.correctOptionId,
                  }"
                >
                  {{ opt.id === r.correctOptionId ? '\u2713' : (opt.id === r.selectedOptionId ? '\u2717' : '') }}
                </span>
                <span>{{ opt.text }}</span>
              </div>
            </div>
            <p v-if="r.explanation" class="inline-quiz-explanation">
              {{ r.explanation }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="inline-quiz-footer">
      <q-btn
        flat
        no-caps
        dense
        label="Continuar leyendo"
        :style="{ color: 'var(--oa-text-secondary)', fontSize: '13px' }"
        @click="handleDismiss"
      />
    </div>
  </div>

  <!-- Answering state -->
  <div v-else-if="state === 'answering'" class="inline-quiz-card">
    <div class="inline-quiz-icon-row" style="margin-bottom: 16px;">
      <span class="inline-quiz-icon">?</span>
      <p class="inline-quiz-title">Mini Quiz</p>
    </div>

    <div class="inline-quiz-questions">
      <div
        v-for="(q, qi) in questions"
        :key="q.id"
        class="inline-quiz-question-card"
      >
        <p class="inline-quiz-question-text">{{ qi + 1 }}. {{ q.question }}</p>
        <div class="inline-quiz-options">
          <button
            v-for="(opt, optIdx) in q.options"
            :key="opt.id"
            class="inline-quiz-option-btn"
            :class="{ 'option-selected': selectedOptions[q.id] === opt.id }"
            @click="selectOption(q.id, opt.id)"
          >
            <span
              class="option-letter"
              :class="{ 'letter-selected': selectedOptions[q.id] === opt.id }"
            >
              {{ LETTERS[optIdx] }}
            </span>
            <span
              class="option-text"
              :class="{ 'text-selected': selectedOptions[q.id] === opt.id }"
            >
              {{ opt.text }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="inline-quiz-footer">
      <q-btn
        no-caps
        dense
        unelevated
        label="Verificar respuestas"
        :disable="!allAnswered"
        :loading="submitting"
        :style="{ background: allAnswered ? 'var(--oa-gold-full)' : 'var(--oa-bg-3)', color: allAnswered ? 'var(--color-marble-cream)' : 'var(--oa-text-muted)', fontWeight: '600', fontSize: '13px', borderRadius: '8px', padding: '6px 16px' }"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { api } from 'boot/axios';

interface InlineQuizBlock {
  type: 'inline_quiz';
  questionIds: string[];
}

interface QuestionData {
  id: string;
  question: string;
  difficulty: number;
  xpReward: number;
  options: { id: string; text: string }[];
}

interface SubmitResultItem {
  questionId: string;
  question: string;
  explanation: string | null;
  options: { id: string; text: string; isCorrect: boolean }[];
  selectedOptionId: string | null;
  correctOptionId: string | null;
  correct: boolean;
}

interface SubmitResult {
  scorePct: number;
  correctAnswers: number;
  totalQuestions: number;
  passed: boolean;
  results: SubmitResultItem[];
}

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

const props = defineProps<{ block: InlineQuizBlock }>();

const state = ref<'idle' | 'loading' | 'answering' | 'submitted'>('idle');
const questions = ref<QuestionData[]>([]);
const selectedOptions = ref<Record<string, string>>({});
const submitResult = ref<SubmitResult | null>(null);
const submitting = ref(false);

const allAnswered = computed(() => {
  return questions.value.length > 0 && Object.keys(selectedOptions.value).length === questions.value.length;
});

async function handleStart() {
  state.value = 'loading';
  try {
    const { data } = await api.get('/quizzes/questions', {
      params: { questionIds: props.block.questionIds.join(',') },
    });
    questions.value = data;
    state.value = 'answering';
  } catch {
    state.value = 'idle';
  }
}

function selectOption(questionId: string, optionId: string) {
  selectedOptions.value = { ...selectedOptions.value, [questionId]: optionId };
}

async function handleSubmit() {
  submitting.value = true;
  try {
    const answers = Object.entries(selectedOptions.value).map(([questionId, selectedOptionId]) => ({
      questionId,
      selectedOptionId,
    }));
    const { data } = await api.post('/quizzes/submit-inline', {
      questionIds: props.block.questionIds,
      answers,
    });
    submitResult.value = data;
    state.value = 'submitted';
  } catch {
    // Stay in answering state on error
  } finally {
    submitting.value = false;
  }
}

function handleDismiss() {
  state.value = 'idle';
  selectedOptions.value = {};
  submitResult.value = null;
  questions.value = [];
}
</script>

<style scoped lang="scss">
.inline-quiz-card {
  background: color-mix(in srgb, var(--oa-gold-full) 3%, var(--oa-bg-2));
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  border-radius: var(--oa-radius-xl);
  padding: 20px;
  margin: 16px 0;
}

.inline-quiz-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.inline-quiz-icon-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.inline-quiz-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--oa-text-sm);
  color: var(--oa-gold-full);
}

.inline-quiz-title {
  font-weight: 600;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  margin: 0;
}

.inline-quiz-count {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

.inline-quiz-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.inline-quiz-score-text {
  font-size: var(--oa-text-sm);
  font-weight: 500;
  color: var(--oa-text-primary);
}

.inline-quiz-results {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}

.inline-quiz-result-card {
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-lg);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  padding: 16px;
}

.inline-quiz-result-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.inline-quiz-result-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}

.result-correct {
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
}

.result-incorrect {
  background: color-mix(in srgb, var(--oa-danger) 10%, transparent);
  color: var(--oa-danger);
}

.inline-quiz-result-body {
  flex: 1;
  min-width: 0;
}

.inline-quiz-result-question {
  font-weight: 500;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  margin: 0 0 8px 0;
}

.inline-quiz-result-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inline-quiz-result-option {
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--oa-text-muted);
  border: 1px solid transparent;
}

.option-correct {
  background: color-mix(in srgb, var(--oa-success) 8%, transparent);
  color: var(--oa-success);
  border-color: color-mix(in srgb, var(--oa-success) 15%, transparent);
}

.option-wrong {
  background: color-mix(in srgb, var(--oa-danger) 8%, transparent);
  color: var(--oa-danger);
  border-color: color-mix(in srgb, var(--oa-danger) 15%, transparent);
}

.option-indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  flex-shrink: 0;
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.indicator-correct {
  border-color: color-mix(in srgb, var(--oa-success) 30%, transparent);
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
}

.indicator-wrong {
  border-color: color-mix(in srgb, var(--oa-danger) 30%, transparent);
  background: color-mix(in srgb, var(--oa-danger) 10%, transparent);
  color: var(--oa-danger);
}

.inline-quiz-explanation {
  font-size: 12px;
  color: var(--oa-text-secondary);
  margin: 8px 0 0 0;
  font-style: italic;
  line-height: 1.5;
}

.inline-quiz-questions {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.inline-quiz-question-card {
  background: var(--oa-bg-2);
  border-radius: var(--oa-radius-lg);
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  padding: 16px;
}

.inline-quiz-question-text {
  font-weight: 500;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  margin: 0 0 12px 0;
}

.inline-quiz-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.inline-quiz-option-btn {
  width: 100%;
  text-align: left;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
  background: transparent;
  cursor: pointer;
  transition: all var(--oa-duration-fast) ease;

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
    background: color-mix(in srgb, var(--oa-gold-full) 3%, transparent);
  }
}

.option-selected {
  border-color: var(--oa-gold-full) !important;
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent) !important;
}

.option-letter {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--oa-bg-3) 50%, transparent);
  color: var(--oa-text-muted);
  transition: all var(--oa-duration-fast) ease;
}

.letter-selected {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
}

.option-text {
  padding-top: 2px;
  color: var(--oa-text-secondary);
  transition: color var(--oa-duration-fast) ease;
}

.text-selected {
  color: var(--oa-text-primary) !important;
  font-weight: 500;
}

.inline-quiz-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
